import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type {
  AssistantActionBase,
  AssistantFeedback,
  AssistantHistoryMessage,
  AssistantMessage,
  AssistantResponse,
} from '../types';
import { applyMessageDecorators } from '../decorators/messageDecorators';
import type { AssistantMessageDecorator } from '../decorators/messageDecorators';

export type AskAIAssistant<TContext, TResponse> = (
  question: string,
  historyForAPI: AssistantHistoryMessage[],
  context?: TContext
) => Promise<TResponse>;

export type AssistantResponseMapper<TResponse, TCitation, TAction> = (
  response: TResponse
) => AssistantResponse<TCitation, TAction>;

export type AssistantActionHandler<TContext, TAction> = (
  action: TAction,
  helpers: {
    submitQuestion: (question: string) => boolean;
    context: TContext;
    setContext?: Dispatch<SetStateAction<TContext>>;
    history: AssistantMessage<unknown, TAction>[];
  }
) => void;

export type AssistantPendingPreviewBuilderInput<TContext, TCitation, TAction> = {
  question: string;
  requestId: number;
  assistantMessageId: string;
  userMessage: AssistantMessage<TCitation, TAction>;
  history: AssistantMessage<TCitation, TAction>[];
  historyForAPI: AssistantHistoryMessage[];
  context: TContext;
};

export type AssistantPendingPreviewBuilder<TContext, TCitation, TAction> = (
  input: AssistantPendingPreviewBuilderInput<TContext, TCitation, TAction>,
) => AssistantMessage<TCitation, TAction> | null | undefined;

export type UseAssistantConversationOptions<TContext, TResponse, TCitation, TAction> = {
  context: TContext;
  setContext?: Dispatch<SetStateAction<TContext>>;
  readStoredContext?: () => Partial<TContext>;
  ask: AskAIAssistant<TContext, TResponse>;
  buildPendingAssistantMessage?: AssistantPendingPreviewBuilder<TContext, TCitation, TAction>;
  cancelCurrentResponse?: () => Promise<void> | void;
  responseMapper?: AssistantResponseMapper<TResponse, TCitation, TAction>;
  decorators?: AssistantMessageDecorator<TContext, TCitation, TAction>[];
  onErrorMessage?: (error: unknown) => string;
  actionHandler?: AssistantActionHandler<TContext, TAction>;
  onFeedbackRequest?: (
    messageId: string,
    feedback: AssistantFeedback,
    message?: AssistantMessage<TCitation, TAction>,
  ) => void;
  allowLocalFeedbackFallback?: boolean;
};

export type UseAssistantConversationResult<TCitation, TAction> = {
  messages: AssistantMessage<TCitation, TAction>[];
  pendingQuestion: string | null;
  errorMessage: string | null;
  isLoading: boolean;
  submitQuestion: (question: string) => boolean;
  appendMessage: (message: AssistantMessage<TCitation, TAction>) => void;
  replaceMessages: (messages: AssistantMessage<TCitation, TAction>[]) => void;
  cancelResponse: () => Promise<boolean>;
  handleFeedback: (messageId: string, feedback: AssistantFeedback) => void;
  handleRetry: (messageId: string) => void;
  handleAction: (action: TAction) => void;
  resetConversation: () => void;
};

const buildHistoryForAPI = (
  history: AssistantMessage[]
): AssistantHistoryMessage[] => {
  const normalized: AssistantHistoryMessage[] = [];

  history.forEach(({ role, content }) => {
    const normalizedRole = role === 'user' || role === 'assistant' ? role : null;
    const normalizedContent = typeof content === 'string' ? content.trim() : '';
    if (!normalizedRole || !normalizedContent) {
      return;
    }
    if (normalized.length === 0 && normalizedRole !== 'user') {
      return;
    }

    const previousMessage = normalized[normalized.length - 1];
    if (previousMessage?.role === normalizedRole) {
      previousMessage.content = `${previousMessage.content}\n\n${normalizedContent}`;
      return;
    }

    normalized.push({
      role: normalizedRole,
      content: normalizedContent,
    });
  });

  return normalized;
};

const defaultErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unable to get a response. Please try again.';
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const defaultActionHandler = <TAction extends AssistantActionBase>(
  action: TAction,
  helpers: {
    submitQuestion: (question: string) => boolean;
  }
) => {
  if (action.type === 'navigate') {
    const href = action.payload && typeof action.payload.href === 'string'
      ? action.payload.href
      : undefined;
    if (href && typeof window !== 'undefined') {
      window.location.assign(href);
    }
    return;
  }
  if (action.type === 'followup') {
    const prompt = action.payload && typeof action.payload.prompt === 'string'
      ? action.payload.prompt
      : undefined;
    if (prompt) {
      helpers.submitQuestion(prompt);
    }
  }
};

export function useAssistantConversation<TContext, TResponse, TCitation, TAction extends AssistantActionBase>(
  options: UseAssistantConversationOptions<TContext, TResponse, TCitation, TAction>
): UseAssistantConversationResult<TCitation, TAction> {
  const {
    context,
    setContext,
    readStoredContext,
    ask,
    buildPendingAssistantMessage,
    cancelCurrentResponse,
    responseMapper,
    decorators = [],
    onErrorMessage,
    actionHandler,
    onFeedbackRequest,
    allowLocalFeedbackFallback = true,
  } = options;

  const [messages, setMessages] = useState<AssistantMessage<TCitation, TAction>[]>([]);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const askRef = useRef(ask);
  const buildPendingAssistantMessageRef = useRef(buildPendingAssistantMessage);
  const contextRef = useRef(context);
  const messagesRef = useRef<AssistantMessage<TCitation, TAction>[]>([]);
  const isLoadingRef = useRef(false);
  const requestCounterRef = useRef(0);
  const activeRequestIdRef = useRef<number | null>(null);
  const activePendingAssistantMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    askRef.current = ask;
  }, [ask]);

  useEffect(() => {
    buildPendingAssistantMessageRef.current = buildPendingAssistantMessage;
  }, [buildPendingAssistantMessage]);

  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const commitMessages = useCallback((
    updater: AssistantMessage<TCitation, TAction>[]
      | ((current: AssistantMessage<TCitation, TAction>[]) => AssistantMessage<TCitation, TAction>[])
  ) => {
    setMessages((current) => {
      const next = typeof updater === 'function'
        ? updater(current)
        : updater;
      messagesRef.current = next;
      return next;
    });
  }, []);

  const runMutation = useCallback(async ({
    requestId,
    question,
    historyForAPI,
    contextOverride,
    pendingAssistantMessageId,
  }: {
    requestId: number;
    question: string;
    historyForAPI: AssistantHistoryMessage[];
    contextOverride?: TContext;
    pendingAssistantMessageId?: string | null;
  }) => {
    isLoadingRef.current = true;
    setIsLoading(true);
    try {
      const effectiveContext = contextOverride ?? contextRef.current;
      const data = await askRef.current(question, historyForAPI, effectiveContext);
      const mapped = responseMapper
        ? responseMapper(data)
        : (data as unknown as AssistantResponse<TCitation, TAction>);
      if (activeRequestIdRef.current !== requestId) {
        return;
      }
      const baseMessage: AssistantMessage<TCitation, TAction> = {
        role: 'assistant',
        content: mapped.answer,
        citations: mapped.citations,
        actions: mapped.actions,
        presentation: mapped.presentation,
        metadata: {
          ...(mapped.metadata ?? {}),
          ...(isRecord((mapped.metadata as Record<string, unknown> | undefined)?.textReveal)
            ? { textReveal: (mapped.metadata as Record<string, unknown>).textReveal }
            : {}),
        },
        messageId: pendingAssistantMessageId ?? `ai-${Date.now()}`,
        timestamp: Date.now(),
      };
      commitMessages((prev) => {
        const decorated = applyMessageDecorators(
          {
            message: baseMessage,
            context: contextRef.current,
            history: [...prev, baseMessage],
            question,
          },
          decorators
        );
        if (pendingAssistantMessageId && prev.some((message) => message.messageId === pendingAssistantMessageId)) {
          return prev.map((message) => (
            message.messageId === pendingAssistantMessageId ? decorated : message
          ));
        }
        return [...prev, decorated];
      });
      activePendingAssistantMessageIdRef.current = null;
      setPendingQuestion(null);
      setErrorMessage(null);
    } catch (error) {
      if (activeRequestIdRef.current !== requestId) {
        return;
      }
      console.error('[AI Assistant] Query failed:', error);
      const failureMessage = onErrorMessage ? onErrorMessage(error) : defaultErrorMessage(error);
      const failureAssistantMessage: AssistantMessage<TCitation, TAction> = {
        role: 'assistant',
        content: failureMessage,
        messageId: pendingAssistantMessageId ?? `ai-error-${Date.now()}`,
        timestamp: Date.now(),
      };
      commitMessages((prev) => (
        pendingAssistantMessageId && prev.some((message) => message.messageId === pendingAssistantMessageId)
          ? prev.map((message) => (
              message.messageId === pendingAssistantMessageId ? failureAssistantMessage : message
            ))
          : [...prev, failureAssistantMessage]
      ));
      activePendingAssistantMessageIdRef.current = null;
      setPendingQuestion(null);
      setErrorMessage(failureMessage);
    } finally {
      if (activeRequestIdRef.current === requestId) {
        activeRequestIdRef.current = null;
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [commitMessages, decorators, onErrorMessage, responseMapper]);

  const submitQuestion = useCallback((question: string): boolean => {
    if (!question.trim() || isLoadingRef.current) {
      return false;
    }

    const trimmedQuestion = question.trim();
    setPendingQuestion(trimmedQuestion);
    setErrorMessage(null);
    const storedContext = readStoredContext ? readStoredContext() : {};
    const effectiveContext = Object.keys(storedContext).length > 0
      ? { ...context, ...storedContext }
      : context;
    if (Object.keys(storedContext).length > 0 && setContext) {
      setContext(effectiveContext);
    }

    const userMessage: AssistantMessage<TCitation, TAction> = {
      role: 'user',
      content: trimmedQuestion,
      messageId: `user-${Date.now()}`,
      timestamp: Date.now(),
    };

    const updatedHistory = [...messagesRef.current, userMessage];
    const requestId = ++requestCounterRef.current;
    activeRequestIdRef.current = requestId;
    const historyForAPI = buildHistoryForAPI(updatedHistory);
    const assistantMessageId = `ai-${Date.now()}-${requestId}`;
    const pendingAssistantMessage = buildPendingAssistantMessageRef.current?.({
      question: trimmedQuestion,
      requestId,
      assistantMessageId,
      userMessage,
      history: updatedHistory,
      historyForAPI,
      context: effectiveContext,
    }) ?? null;
    const normalizedPendingAssistantMessage = pendingAssistantMessage
      ? {
          ...pendingAssistantMessage,
          role: 'assistant',
          messageId: pendingAssistantMessage.messageId ?? assistantMessageId,
          timestamp: pendingAssistantMessage.timestamp ?? Date.now(),
        } satisfies AssistantMessage<TCitation, TAction>
      : null;
    activePendingAssistantMessageIdRef.current = normalizedPendingAssistantMessage?.messageId ?? null;
    commitMessages(
      normalizedPendingAssistantMessage
        ? [...updatedHistory, normalizedPendingAssistantMessage]
        : updatedHistory,
    );

    void runMutation({
      requestId,
      question: trimmedQuestion,
      historyForAPI,
      contextOverride: effectiveContext,
      pendingAssistantMessageId: normalizedPendingAssistantMessage?.messageId ?? null,
    });
    return true;
  }, [commitMessages, context, readStoredContext, runMutation, setContext]);

  const appendMessage = useCallback((message: AssistantMessage<TCitation, TAction>) => {
    commitMessages((prev) => [...prev, message]);
  }, [commitMessages]);

  const replaceMessages = useCallback((nextMessages: AssistantMessage<TCitation, TAction>[]) => {
    activeRequestIdRef.current = null;
    activePendingAssistantMessageIdRef.current = null;
    isLoadingRef.current = false;
    setIsLoading(false);
    setPendingQuestion(null);
    setErrorMessage(null);
    commitMessages([...nextMessages]);
  }, [commitMessages]);

  const cancelResponse = useCallback(async (): Promise<boolean> => {
    if (!isLoadingRef.current) {
      return false;
    }

    activeRequestIdRef.current = null;
    const pendingAssistantMessageId = activePendingAssistantMessageIdRef.current;
    activePendingAssistantMessageIdRef.current = null;
    isLoadingRef.current = false;
    setIsLoading(false);
    setPendingQuestion(null);
    setErrorMessage(null);
    if (pendingAssistantMessageId) {
      commitMessages((prev) => prev.filter((message) => message.messageId !== pendingAssistantMessageId));
    }

    try {
      await cancelCurrentResponse?.();
      return true;
    } catch (error) {
      const failureMessage = onErrorMessage ? onErrorMessage(error) : defaultErrorMessage(error);
      setErrorMessage(failureMessage);
      console.error('[AI Assistant] Cancel failed:', error);
      return false;
    }
  }, [cancelCurrentResponse, onErrorMessage]);

  const handleFeedback = useCallback((messageId: string, feedback: AssistantFeedback) => {
    const targetMessage = messagesRef.current.find((message) => message.messageId === messageId);
    onFeedbackRequest?.(messageId, feedback, targetMessage);
    if (!allowLocalFeedbackFallback) {
      return;
    }
    commitMessages((prev) => prev.map((message) => (
      message.messageId === messageId ? { ...message, feedback } : message
    )));
  }, [allowLocalFeedbackFallback, commitMessages, onFeedbackRequest]);

  const handleRetry = useCallback((messageId: string) => {
    const currentMessages = messagesRef.current;
    const targetIndex = currentMessages.findIndex((message) => message.messageId === messageId);
    if (targetIndex === -1) {
      return;
    }
    const previousQuestion = [...currentMessages]
      .slice(0, targetIndex)
      .reverse()
      .find((message) => message.role === 'user');

    if (!previousQuestion?.content) {
      return;
    }

    if (targetIndex !== currentMessages.length - 1) {
      submitQuestion(previousQuestion.content);
      return;
    }

    const baseHistory = currentMessages.slice(0, targetIndex);
    commitMessages(baseHistory);
    setPendingQuestion(previousQuestion.content);
    setErrorMessage(null);

    const requestId = ++requestCounterRef.current;
    activeRequestIdRef.current = requestId;
    void runMutation({
      requestId,
      question: previousQuestion.content,
      historyForAPI: buildHistoryForAPI(baseHistory),
    });
  }, [commitMessages, runMutation, submitQuestion]);

  const handleAction = useCallback((action: TAction) => {
    if (actionHandler) {
      actionHandler(action, {
        submitQuestion,
        context,
        setContext,
        history: messagesRef.current,
      });
      return;
    }
    defaultActionHandler(action, { submitQuestion });
  }, [actionHandler, context, setContext, submitQuestion]);

  const resetConversation = useCallback(() => {
    if (isLoadingRef.current) {
      void cancelCurrentResponse?.();
    }
    activeRequestIdRef.current = null;
    activePendingAssistantMessageIdRef.current = null;
    isLoadingRef.current = false;
    setIsLoading(false);
    commitMessages([]);
    setPendingQuestion(null);
    setErrorMessage(null);
  }, [cancelCurrentResponse, commitMessages]);

  return {
    messages,
    pendingQuestion,
    errorMessage,
    isLoading,
    submitQuestion,
    appendMessage,
    replaceMessages,
    cancelResponse,
    handleFeedback,
    handleRetry,
    handleAction,
    resetConversation,
  };
}
